import React from "react";
import {
  Grid,
  TextField,
  Typography,
  Box,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  IconButton,
  Autocomplete,
  FormControl,
  FormHelperText,
} from "@mui/material";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import config from "../../config";
import CloseIcon from "@mui/icons-material/Close";
import { isMobile } from "../../globalStyles/mobile-design";

const defaultImage = "/product.png";

const ProductDetailsModal = ({ product, open, onClose }) => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const fetchImage = (images) => {
    if (images.length) {
      const featureImage = images?.find((img) => img.feature_image);
      const chosen = featureImage || images[0];
      if (chosen?.url) {
        return chosen.url;
      } else if (chosen?.image) {
        return `${config.baseURL}/fetch-file/${chosen.image}`;
      }
    }
    return defaultImage;
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <DialogTitle
            fontSize={"20px"}
            fontFamily={"poppins"}
            fontWeight={500}
            color={"#333333"}
          >
            {"Product Details"}
          </DialogTitle>
          <IconButton onClick={() => onClose()}>
            <CloseIcon />
          </IconButton>
        </div>
        <Divider style={{ marginBottom: 10 }} />
        <DialogContent
          sx={{
            overflowY: "auto",
            "&::WebkitScrollbar": {
              width: "7px",
            },
            "&::WebkitScrollbar-track": {
              backgroundColor: "#D9D9D9",
              borderRadius: 14,
            },
            "&::WebkitScrollbar-thumb": {
              backgroundColor: "#273894",
              borderRadius: "6px",
              "&:hover": {
                backgroundColor: "#555",
              },
            },
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography
                fontSize={"14px"}
                fontFamily={"poppins"}
                fontWeight={400}
              >
                Name
              </Typography>
              <TextField
                size="small"
                autoFocus
                margin="dense"
                variant="outlined"
                type="text"
                fullWidth
                value={product?.name || ""}
                InputProps={{
                  readOnly: true,
                }}
                sx={{
                  "& .MuiInputBase-input": {
                    color: "#7C7C7C",
                  },
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#f0f0f0",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                fontSize={"14px"}
                fontFamily={"poppins"}
                fontWeight={400}
              >
                Category
              </Typography>
              <TextField
                size="small"
                margin="dense"
                placeholder="Type"
                type="text"
                fullWidth
                value={product?.product_type || ""}
                InputProps={{
                  readOnly: true,
                }}
                sx={{
                  "& .MuiInputBase-input": {
                    color: "#7C7C7C",
                  },
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#f0f0f0",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                fontSize={"14px"}
                fontFamily={"poppins"}
                fontWeight={400}
              >
                Short Description
              </Typography>
              <TextField
                size="small"
                margin="dense"
                placeholder="Short Description"
                type="text"
                fullWidth
                value={product?.short_description || ""}
                InputProps={{
                  readOnly: true,
                }}
                sx={{
                  "& .MuiInputBase-input": {
                    color: "#7C7C7C",
                  },
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#f0f0f0",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                fontSize={"14px"}
                fontFamily={"poppins"}
                fontWeight={400}
              >
                PGA Flag
              </Typography>
              <TextField
                size="small"
                margin="dense"
                placeholder="PGA Flag"
                type="text"
                fullWidth
                value={product?.pga_flag || ""}
                InputProps={{
                  readOnly: true,
                }}
                sx={{
                  "& .MuiInputBase-input": {
                    color: "#7C7C7C",
                  },
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#f0f0f0",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <Typography
                fontSize={"14px"}
                fontFamily={"poppins"}
                fontWeight={400}
              >
                URL
              </Typography>
              <TextField
                size="small"
                margin="dense"
                placeholder="Product URL"
                type="text"
                fullWidth
                value={product?.url || ""}
                InputProps={{
                  readOnly: true,
                }}
                sx={{
                  "& .MuiInputBase-input": {
                    color: "#7C7C7C",
                  },
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#f0f0f0",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <Typography
                fontSize={"14px"}
                fontFamily={"poppins"}
                fontWeight={400}
              >
                Description
              </Typography>
              <ReactQuill
                theme="snow"
                value={product?.description || ""}
                readOnly={true}
                modules={{ toolbar: false }}
                style={{
                  height: isMobile() ? 70 : "120px",
                  marginBottom: "50px",
                  marginTop: 10,
                }}
              />
              <style>
                {`
          .ql-container {
            background-color: #f0f0f0 !important;
            height: 100%; 
            color:#7C7C7C !important
          }
          .ql-editor {
            min-height: 100%; 
            background-color: #f0f0f0 !important; 
            padding: 10px; 
            border-radius: 8px;
            color:#7C7C7C !important
          }
        `}
              </style>
            </Grid>
            <Grid item xs={12} sm={12} sx={{ marginTop: isMobile() ? 5 : 0 }}>
              <Typography
                fontSize={"14px"}
                fontFamily={"poppins"}
                fontWeight={400}
              >
                Media
              </Typography>
              <Box
                sx={{
                  width: "100%",
                  borderWidth: 1,
                  minHeight: 100,
                  borderColor: "#D1D1D1",
                  borderRadius: 4,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  mt: { xs: 2, sm: 1 },
                  backgroundColor: "#F0F0F0",
                }}
              >
                {product?.images?.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      flexWrap: "wrap",
                      mt: 2,
                      m: 3,
                      overflowX: "auto",
                      maxHeight: 250,
                      border: "1px solid #000",
                      padding: "10px",
                      borderRadius: 1,
                      minWidth: { xs: 200, sm: 300 },
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 2,
                        mt: 2,
                      }}
                    >
                      {product?.images?.map((image, index) => (
                        <Box
                          key={index}
                          display="flex"
                          alignItems="center"
                          mb={2}
                          mr={2}
                          p={1}
                          border={1}
                          borderRadius={4}
                          height={150}
                          ml={2}
                          flexDirection="row"
                          flexWrap="wrap"
                        >
                          <img
                            src={fetchImage(image)}
                            alt={`Uploaded Image ${index}`}
                            style={{
                              maxWidth: "100px",
                              maxHeight: "100px",
                              marginRight: "10px",
                            }}
                          />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography
                fontSize={"14px"}
                fontFamily={"poppins"}
                fontWeight={400}
              >
                Currency
              </Typography>
              <TextField
                size="small"
                margin="dense"
                placeholder="Currency"
                type="text"
                fullWidth
                value={product?.currency || ""}
                InputProps={{
                  readOnly: true,
                }}
                sx={{
                  "& .MuiInputBase-input": {
                    color: "#7C7C7C",
                  },
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#f0f0f0",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography
                fontSize={"14px"}
                fontFamily={"poppins"}
                fontWeight={400}
              >
                Status
              </Typography>
              <TextField
                size="small"
                margin="dense"
                placeholder="Status"
                type="text"
                fullWidth
                value={product?.status || ""}
                InputProps={{
                  readOnly: true,
                }}
                sx={{
                  "& .MuiInputBase-input": {
                    color: "#7C7C7C",
                  },
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#f0f0f0",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography
                fontSize={"14px"}
                fontFamily={"poppins"}
                fontWeight={400}
              >
                Supplier
              </Typography>
              <TextField
                size="small"
                margin="dense"
                placeholder="Supplier"
                type="text"
                fullWidth
                value={product?.vendor || ""}
                InputProps={{
                  readOnly: true,
                }}
                sx={{
                  "& .MuiInputBase-input": {
                    color: "#7C7C7C",
                  },
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#f0f0f0",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <Typography
                fontSize={"14px"}
                fontFamily={"poppins"}
                fontWeight={400}
              >
                Variants
              </Typography>
              {product?.variants?.map((variant, index) => (
                <Card
                  key={index}
                  style={{ marginBottom: 16, backgroundColor: "#EEEEEE" }}
                >
                  <CardContent>
                    <Grid sx={{ marginTop: 2 }} container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <Typography
                          fontSize={"14px"}
                          fontFamily={"poppins"}
                          fontWeight={400}
                        >
                          Price
                        </Typography>
                        <TextField
                          margin="dense"
                          // type="number"
                          size="small"
                          placeholder="Price"
                          value={
                            variant?.price ? `$${variant.price.toFixed(2)}` : ""
                          }
                          fullWidth
                          InputProps={{
                            readOnly: true,
                          }}
                          sx={{
                            "& .MuiInputBase-input": {
                              color: "#7C7C7C",
                            },
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "#f0f0f0",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography
                          fontSize={"14px"}
                          fontFamily={"poppins"}
                          fontWeight={400}
                        >
                          SKU
                        </Typography>
                        <TextField
                          margin="dense"
                          type="text"
                          size="small"
                          placeholder="SKU"
                          value={variant?.sku || ""}
                          fullWidth
                          InputProps={{
                            readOnly: true,
                          }}
                          sx={{
                            "& .MuiInputBase-input": {
                              color: "#7C7C7C",
                            },
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "#f0f0f0",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography
                          fontSize={"14px"}
                          fontFamily={"poppins"}
                          fontWeight={400}
                        >
                          Inventory
                        </Typography>
                        <TextField
                          margin="dense"
                          type="number"
                          size="small"
                          placeholder="Inventory"
                          value={variant?.inventory || ""}
                          fullWidth
                          InputProps={{
                            readOnly: true,
                          }}
                          sx={{
                            "& .MuiInputBase-input": {
                              color: "#7C7C7C",
                            },
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "#f0f0f0",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography
                          fontSize={"14px"}
                          fontFamily={"poppins"}
                          fontWeight={400}
                        >
                          HS Code
                        </Typography>
                        <TextField
                          margin="dense"
                          size="small"
                          placeholder="HS Code"
                          value={variant?.hs_code || ""}
                          fullWidth
                          InputProps={{
                            readOnly: true,
                          }}
                          sx={{
                            "& .MuiInputBase-input": {
                              color: "#7C7C7C",
                            },
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "#f0f0f0",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography
                          fontSize={"14px"}
                          fontFamily={"poppins"}
                          fontWeight={400}
                        >
                          HTS Code
                        </Typography>
                        <TextField
                          margin="dense"
                          size="small"
                          placeholder="HTS Code"
                          value={variant?.hts_code || ""}
                          fullWidth
                          InputProps={{
                            readOnly: true,
                          }}
                          sx={{
                            "& .MuiInputBase-input": {
                              color: "#7C7C7C",
                            },
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "#f0f0f0",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography
                          fontSize={"14px"}
                          fontFamily={"poppins"}
                          fontWeight={400}
                        >
                          Country of Origin
                        </Typography>
                        <TextField
                          margin="dense"
                          size="small"
                          placeholder="Country of Origin"
                          value={variant?.country_of_origin || ""}
                          fullWidth
                          InputProps={{
                            readOnly: true,
                          }}
                          sx={{
                            "& .MuiInputBase-input": {
                              color: "#7C7C7C",
                            },
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "#f0f0f0",
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))}
              {user?.role === "admin" ? (
                <Grid item xs={12} sm={12}>
                  <Typography
                    fontSize={"14px"}
                    fontFamily={"poppins"}
                    fontWeight={400}
                  >
                    Client
                  </Typography>
                  <TextField
                    type="text"
                    size="small"
                    margin="dense"
                    placeholder="Client Name"
                    value={product?.client_id?.business_name || ""}
                    fullWidth
                    InputProps={{
                      readOnly: true,
                    }}
                    sx={{
                      "& .MuiInputBase-input": {
                        color: "#7C7C7C",
                      },
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#f0f0f0",
                      },
                    }}
                  />
                </Grid>
              ) : (
                <></>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions style={{ padding: 10 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            style={{ textTransform: "none" }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProductDetailsModal;
